/**
 * AdminTeamPanel — super-admin-only.
 *
 * Two areas:
 *  1) Admins & Managers list (grant, revoke, change role, employee profile,
 *     photo, CV, monthly salary slip via Resend email attachment).
 *  2) Partnerships & shareholders (add/delete free-form entries).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, UserPlus, Trash2, X, Loader2, Upload, Mail, Image as ImageIcon,
  FileText, ChevronRight, Handshake, Briefcase, Save, ExternalLink,
  Plus, Minus, FileSignature, Download, LayoutGrid,
} from 'lucide-react';
import { useAdminLang } from '../hooks/useAdminLang';

const CONTRACT_TYPES = ['CDI', 'CDD', 'Stage', 'Freelance', 'Alternance', 'Autre'];

/** v50.18b — quick-action tiles a plain admin can be granted access to
 * (the "Actions Rapides" zone, from update-notifications down to before the leaderboard). */
const SECTION_DEFS = (at) => ([
  ['update_notif', at('tileUpdateNotifTitle') || 'Notifications de mise à jour'],
  ['weekly_cinema', at('tileWeeklyCinemaTitle') || 'Cinéma de la semaine'],
  ['admin_email', at('tileEmailTitle') || 'Envoyer un email'],
  ['users', at('tileUsersTitle') || 'Utilisateurs'],
  ['badges', at('tileBadgeTitle') || 'Badges'],
  ['badge_candidates', at('tileBadgeCandidatesTitle') || 'Candidats badges'],
  ['myia_insights', at('tileMyIaInsightsTitle') || 'MyIA Insights'],
  ['moderation', at('tileModerationTitle') || 'Modération'],
  ['cinema', at('tileCinemaTitle') || 'Cinéma'],
  ['franchises', at('tileFranchisesTitle') || 'Franchises'],
  ['tmdb_sync', at('tileTmdbTitle') || 'Synchro TMDB'],
  ['free_films', at('tileFreeFilmTitle') || 'Films gratuits'],
  ['ads_validation', at('tileAdsValidationTitle') || 'Validation des pubs'],
  ['monetization', at('tileMonetizationTitle') || 'Monétisation'],
  ['ads_pro', at('tileAdsTitle') || 'Destine Ads'],
  ['staff_chat', 'Chat du Personnel'],
  ['analytics', 'Analytiques'],
  ['trends', 'Tendances'],
  ['reports', 'Signalements'],
  ['group_reports', 'Signalements Groupes'],
  ['accounting', 'Comptabilité'],
  ['myshop', 'MyShop'],
  ['esim', 'eSIM (Airalo)'],
  ['myjob', 'MyJob — Offres'],
  ['legal_docs', at('legalDocsTileTitle') || 'Documents juridiques'],
  ['edepots', at('edepotsTileTitle') || 'e-Dépôts'],
  ['mymouv_events', at('mymouvEventsTile') || 'MyMouv — Événements & billetterie'],
]);

const SectionChecklist = ({ at, value, onChange }) => (
  <div className="grid grid-cols-2 gap-1.5" data-testid="team-sections-checklist">
    {SECTION_DEFS(at).map(([id, label]) => (
      <label key={id} className="flex items-center gap-2 text-xs text-white/80 cursor-pointer px-2 py-1.5 rounded-lg hover:bg-white/5">
        <input
          type="checkbox"
          checked={value.includes(id)}
          onChange={() => onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id])}
          className="accent-fuchsia-500"
          data-testid={`team-section-check-${id}`}
        />
        {label}
      </label>
    ))}
  </div>
);

const RoleBadge = ({ role }) => (
  <span className={`text-[10px] px-2 py-0.5 rounded-full ${role === 'manager' ? 'bg-amber-500/25 text-amber-200' : 'bg-fuchsia-500/25 text-fuchsia-200'}`}>
    {role === 'manager' ? 'Manager' : 'Admin'}
  </span>
);

const AdminTeamPanel = () => {
  const { at } = useAdminLang();
  const [tab, setTab] = useState('team'); // team | partners
  const [team, setTeam] = useState({ admins: [], managers: [] });
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGrant, setShowGrant] = useState(false);
  const [openMember, setOpenMember] = useState(null); // detailed edit
  const [openSections, setOpenSections] = useState(null); // sections editor (plain admins)
  const [photoFull, setPhotoFull] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        axios.get('/api/admin/team'),
        axios.get('/api/admin/partners'),
      ]);
      setTeam({ admins: t.data?.admins || [], managers: t.data?.managers || [] });
      setPartners(p.data?.partners || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (m) => {
    if (!window.confirm(at('teamConfirmRevoke') || `Retirer l'accès admin à ${m.name} ?`)) return;
    try {
      await axios.delete(`/api/admin/team/${m.id}`);
      toast.success(at('teamRevoked') || 'Accès révoqué');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const setRole = async (m, role) => {
    try {
      await axios.post(`/api/admin/team/${m.id}/role`, { role });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const all = [...team.admins, ...team.managers];

  return (
    <div className="space-y-4" data-testid="admin-team-panel">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setTab('team')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${tab === 'team' ? 'bg-fuchsia-500 border-fuchsia-400 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
          data-testid="team-tab-team"
        >
          <Users className="w-3.5 h-3.5 inline mr-1" /> {at('teamTabTeam') || 'Équipe'} ({all.length})
        </button>
        <button
          onClick={() => setTab('partners')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${tab === 'partners' ? 'bg-fuchsia-500 border-fuchsia-400 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
          data-testid="team-tab-partners"
        >
          <Handshake className="w-3.5 h-3.5 inline mr-1" /> {at('teamTabPartners') || 'Partenaires & actionnaires'} ({partners.length})
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : tab === 'team' ? (
        <>
          <button
            onClick={() => setShowGrant(true)}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 text-white text-sm font-semibold flex items-center gap-2"
            data-testid="team-grant-btn"
          >
            <UserPlus className="w-4 h-4" /> {at('teamGrant') || 'Donner accès admin à un utilisateur'}
          </button>

          {all.length === 0 ? (
            <div className="text-center py-8 text-white/40 border border-dashed border-white/10 rounded-xl">
              {at('teamEmpty') || 'Aucun admin/manager encore.'}
            </div>
          ) : (
            <ul className="space-y-2" data-testid="team-list">
              {all.map(m => (
                <li key={m.id} className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid={`team-member-${m.id}`}>
                  <div className="flex items-center gap-3">
                    {m.profile?.photo_url || m.avatar_url ? (
                      <img src={m.avatar_url} alt={m.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-white/60 font-semibold">
                        {m.name?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white text-sm">{m.name}</span>
                        <RoleBadge role={m.role} />
                        {m.role === 'admin' && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-200" data-testid={`team-sections-count-${m.id}`}>
                            {(m.sections || []).length} {at('teamSectionsShort') || 'sections'}
                          </span>
                        )}
                        {m.profile?.job_type && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60">{m.profile.job_type}</span>
                        )}
                      </div>
                      <div className="text-xs text-white/50">{m.email}</div>
                    </div>
                    <div className="flex gap-1">
                      <select
                        value={m.role}
                        onChange={(e) => setRole(m, e.target.value)}
                        className="text-xs bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white/80"
                        data-testid={`team-role-${m.id}`}
                      >
                        <option value="admin">Admin</option>
                        <option value="manager">Manager</option>
                      </select>
                      {m.role === 'admin' && (
                        <button onClick={() => setOpenSections(m)} className="p-1.5 rounded-lg hover:bg-white/10 text-sky-300" title={at('teamSectionsBtn') || 'Sections attribuées'} data-testid={`team-sections-${m.id}`}>
                          <LayoutGrid className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => setOpenMember(m)} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60" title="Profil employé" data-testid={`team-edit-${m.id}`}>
                        <Briefcase className="w-4 h-4" />
                      </button>
                      <button onClick={() => revoke(m)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-300" title="Révoquer" data-testid={`team-revoke-${m.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <PartnersSection at={at} partners={partners} reload={load} />
      )}

      {showGrant && <GrantModal at={at} onClose={() => setShowGrant(false)} onDone={async () => { setShowGrant(false); await load(); }} />}
      {openSections && (
        <SectionsModal
          at={at}
          member={openSections}
          onClose={() => setOpenSections(null)}
          onDone={async () => { setOpenSections(null); await load(); }}
        />
      )}
      {openMember && (
        <MemberModal
          at={at}
          member={openMember}
          onClose={() => setOpenMember(null)}
          onDone={async () => { await load(); }}
          onPhotoClick={(u) => setPhotoFull(u)}
        />
      )}
      {photoFull && (
        <div className="fixed inset-0 z-[10000] bg-black/95 flex items-center justify-center p-4" onClick={() => setPhotoFull(null)} data-testid="team-photo-fullscreen">
          <button onClick={() => setPhotoFull(null)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><X className="w-5 h-5" /></button>
          <img src={photoFull} alt="employee" className="max-w-full max-h-full object-contain rounded-2xl" />
        </div>
      )}
    </div>
  );
};

// -------------------- Grant modal --------------------
const GrantModal = ({ at, onClose, onDone }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [sections, setSections] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await axios.post('/api/admin/team/grant', { email: email.trim(), role, sections: role === 'admin' ? sections : [] });
      toast.success(at('teamGranted') || 'Accès accordé');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-neutral-900 border border-white/10 p-5" onClick={e => e.stopPropagation()} data-testid="team-grant-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">{at('teamGrant') || 'Donner accès admin'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <input
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="email@exemple.com"
          className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm mb-3"
          data-testid="team-grant-email"
        />
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-white/70">Rôle:</span>
          <select value={role} onChange={e => setRole(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-sm" data-testid="team-grant-role">
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
          </select>
        </div>
        {role === 'admin' && (
          <div className="mb-4">
            <p className="text-xs text-white/60 mb-2">{at('teamSectionsLabel') || 'Sections (Actions rapides) accessibles à cet admin :'}</p>
            <SectionChecklist at={at} value={sections} onChange={setSections} />
          </div>
        )}
        <button
          onClick={submit}
          disabled={busy || !email.trim()}
          className="w-full py-2.5 rounded-lg bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2"
          data-testid="team-grant-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          {at('teamGrantConfirm') || 'Accorder'}
        </button>
      </div>
    </div>
  );
};

// -------------------- Sections modal (assign quick-action sections to a plain admin) --------------------
const SectionsModal = ({ at, member, onClose, onDone }) => {
  const [sections, setSections] = useState(member.sections || []);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`/api/admin/team/${member.id}/sections`, { sections });
      toast.success(at('teamSectionsSaved') || 'Sections mises à jour');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-neutral-900 border border-white/10 p-5" onClick={e => e.stopPropagation()} data-testid="team-sections-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold flex items-center gap-2"><LayoutGrid className="w-4 h-4 text-sky-300" /> {at('teamSectionsBtn') || 'Sections attribuées'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="team-sections-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-white/50 mb-3">{member.name} · {member.email}</p>
        <p className="text-xs text-white/60 mb-2">{at('teamSectionsLabel') || 'Sections (Actions rapides) accessibles à cet admin :'}</p>
        <SectionChecklist at={at} value={sections} onChange={setSections} />
        <button
          onClick={save}
          disabled={busy}
          className="mt-4 w-full py-2.5 rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 hover:brightness-110 disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2"
          data-testid="team-sections-save"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {at('teamSectionsSave') || 'Enregistrer'}
        </button>
      </div>
    </div>
  );
};

// -------------------- Member (employee profile) modal --------------------
const MemberModal = ({ at, member, onClose, onDone, onPhotoClick }) => {
  const [tab, setTab] = useState('profile'); // profile | slips
  const [profile, setProfile] = useState({
    contract_type: member.profile?.contract_type || 'CDI',
    hours_per_week: member.profile?.hours_per_week || 35,
    job_type: member.profile?.job_type || '',
    entry_date: member.profile?.entry_date || '',
    salary_email: member.profile?.salary_email || member.email || '',
  });
  const [photoUrl, setPhotoUrl] = useState(member.photo_id ? `/api/admin/team/files/${member.photo_id}` : null);
  const [cvId, setCvId] = useState(member.cv_id || null);
  const [busy, setBusy] = useState(false);

  const saveProfile = async () => {
    setBusy(true);
    try {
      await axios.put(`/api/admin/team/${member.id}/profile`, profile);
      toast.success(at('teamProfileSaved') || 'Profil enregistré');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`/api/admin/team/${member.id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotoUrl(`/api/admin/team/files/${r.data.id}`);
      toast.success(at('teamPhotoUploaded') || 'Photo mise à jour');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const uploadCv = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`/api/admin/team/${member.id}/cv`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCvId(r.data.id);
      toast.success(at('teamCvUploaded') || 'CV importé');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-neutral-900 border border-white/10 p-5 my-8 relative" onClick={e => e.stopPropagation()} data-testid="team-member-modal">
        {/* Sticky header with close button — always reachable while scrolling */}
        <div className="sticky top-0 z-10 -mx-5 px-5 -mt-5 pt-5 pb-3 bg-neutral-900/95 backdrop-blur border-b border-white/10 mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg">{member.name}</h3>
            <p className="text-xs text-white/50">{member.email}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white/5 hover:bg-red-500/25 hover:text-red-200 border border-white/10" data-testid="team-edit-close" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab('profile')} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tab === 'profile' ? 'bg-fuchsia-500 text-white' : 'bg-white/5 text-white/70'}`} data-testid="member-tab-profile">
            {at('teamJobType') || 'Profil'}
          </button>
          <button onClick={() => setTab('slips')} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tab === 'slips' ? 'bg-fuchsia-500 text-white' : 'bg-white/5 text-white/70'}`} data-testid="member-tab-slips">
            {at('slipsTab') || 'Mes bulletins'}
          </button>
        </div>

        {tab === 'slips' ? (
          <SlipsArchive at={at} memberId={member.id} />
        ) : (
        <>
        {/* Photo */}
        <div className="mb-4">
          <label className="text-xs text-white/60 block mb-2">{at('teamPhoto') || 'Photo'}</label>
          <div className="flex items-center gap-3">
            {photoUrl ? (
              <img src={photoUrl} alt="employee" onClick={() => onPhotoClick(photoUrl)} className="w-20 h-20 rounded-2xl object-cover cursor-zoom-in border border-white/15" data-testid="team-photo-thumb" />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-white/5 border border-dashed border-white/20 flex items-center justify-center text-white/40"><ImageIcon className="w-6 h-6" /></div>
            )}
            <label className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-sm cursor-pointer flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" /> {at('teamPhotoUpload') || 'Importer'}
              <input type="file" accept="image/*" hidden onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0])} data-testid="team-photo-input" />
            </label>
          </div>
        </div>

        {/* Employee profile fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs text-white/60 block mb-1">{at('teamContract') || 'Type de contrat'}</label>
            <select value={profile.contract_type} onChange={e => setProfile(p => ({ ...p, contract_type: e.target.value }))} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="team-contract-input">
              {CONTRACT_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-white/60 block mb-1">{at('teamHours') || 'Heures / semaine'}</label>
            <input type="number" min="0" max="80" step="0.5" value={profile.hours_per_week} onChange={e => setProfile(p => ({ ...p, hours_per_week: parseFloat(e.target.value) || 0 }))} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="team-hours-input" />
          </div>
          <div>
            <label className="text-xs text-white/60 block mb-1">{at('teamJobType') || 'Poste'}</label>
            <input type="text" value={profile.job_type} onChange={e => setProfile(p => ({ ...p, job_type: e.target.value }))} placeholder="ex: Developer, Marketing…" className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="team-job-input" />
          </div>
          <div>
            <label className="text-xs text-white/60 block mb-1">{at('teamEntryDate') || "Date d'entrée"}</label>
            <input type="date" value={profile.entry_date} onChange={e => setProfile(p => ({ ...p, entry_date: e.target.value }))} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="team-entrydate-input" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-white/60 block mb-1">{at('teamSalaryEmail') || 'Email pour bulletin de salaire'}</label>
            <input type="email" value={profile.salary_email} onChange={e => setProfile(p => ({ ...p, salary_email: e.target.value }))} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="team-salary-email-input" />
          </div>
        </div>

        <button
          onClick={saveProfile}
          disabled={busy}
          className="w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2 mb-4"
          data-testid="team-profile-save-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {at('teamProfileSave') || 'Enregistrer le profil'}
        </button>

        {/* CV upload */}
        <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-fuchsia-300" />
              <span className="text-sm font-medium">CV</span>
              {cvId && (
                <a href={`/api/admin/team/files/${cvId}`} target="_blank" rel="noopener noreferrer" className="text-xs text-fuchsia-300 hover:underline flex items-center gap-1" data-testid="team-cv-view">
                  <ExternalLink className="w-3 h-3" /> {at('teamCvView') || 'Ouvrir'}
                </a>
              )}
            </div>
            <label className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/15 text-xs cursor-pointer flex items-center gap-1">
              <Upload className="w-3 h-3" /> {cvId ? (at('teamCvReplace') || 'Remplacer') : (at('teamCvUpload') || 'Importer')}
              <input type="file" accept="application/pdf" hidden onChange={e => e.target.files?.[0] && uploadCv(e.target.files[0])} data-testid="team-cv-input" />
            </label>
          </div>
        </div>

        {/* Salary slip */}
        <SalarySlipBox at={at} memberId={member.id} />
        {/* Pay slip generator (v50.5) */}
        <GenerateSalarySlipBox at={at} memberId={member.id} member={member} />
        </>
        )}
      </div>
    </div>
  );
};

// -------------------- Slips archive --------------------
const SlipsArchive = ({ at, memberId }) => {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/admin/team/${memberId}/slips`);
      setSlips(r.data?.slips || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [memberId]);
  useEffect(() => { load(); }, [load]);

  const download = (s) => {
    const url = `/api/admin/team/slips/download/${s.id}`;
    window.open(url, '_blank');
  };

  const del = async (s) => {
    if (!window.confirm(at('slipsConfirmDelete') || 'Supprimer ce bulletin ?')) return;
    try {
      await axios.delete(`/api/admin/team/slips/${s.id}`);
      toast.success('OK');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (slips.length === 0) return <div className="text-center py-8 text-white/40 border border-dashed border-white/10 rounded-xl">{at('slipsEmpty') || 'Aucun bulletin archivé.'}</div>;
  const sorted = [...slips].reverse();
  return (
    <ul className="space-y-2" data-testid="slips-archive">
      {sorted.map(s => (
        <li key={s.id} className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center gap-3" data-testid={`slip-${s.id}`}>
          <div className="w-9 h-9 rounded-lg bg-fuchsia-500/20 text-fuchsia-300 flex items-center justify-center flex-shrink-0"><FileText className="w-4 h-4" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-white text-sm">{s.period} {s.net_amount ? `· ${s.net_amount} €` : ''}</div>
            <div className="text-[11px] text-white/50">{new Date(s.sent_at).toLocaleString()} → {s.sent_to}</div>
          </div>
          <button onClick={() => download(s)} className="p-1.5 rounded-lg hover:bg-emerald-500/20 text-emerald-300" title={at('slipsDownload')} data-testid={`slip-dl-${s.id}`}>
            <ExternalLink className="w-4 h-4" />
          </button>
          <button onClick={() => del(s)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-300" title={at('slipsDelete')} data-testid={`slip-del-${s.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </li>
      ))}
    </ul>
  );
};

// -------------------- Salary slip sender --------------------
const SalarySlipBox = ({ at, memberId }) => {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [netAmount, setNetAmount] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!file) { toast.error(at('teamSlipFileRequired') || 'PDF requis'); return; }
    if (!/^\d{4}-\d{2}$/.test(period)) { toast.error(at('teamSlipPeriodInvalid') || 'Période invalide (YYYY-MM)'); return; }
    setSending(true);
    const fd = new FormData();
    fd.append('period', period);
    if (netAmount) fd.append('net_amount', netAmount);
    fd.append('file', file);
    try {
      const r = await axios.post(`/api/admin/team/${memberId}/salary-slip`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data?.sent) toast.success((at('teamSlipSent') || 'Bulletin envoyé à {to}').replace('{to}', r.data.to));
      else toast.warning(at('teamSlipStored') || 'Bulletin archivé (Resend non configuré)');
      setFile(null);
      setNetAmount('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-400/25" data-testid="team-slip-box">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-emerald-300" />
        <span className="text-sm font-medium text-emerald-200">{at('teamSlip') || 'Envoyer un bulletin de salaire par email'}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="team-slip-period" />
        <input type="text" value={netAmount} onChange={e => setNetAmount(e.target.value)} placeholder="Net (ex: 2450.00)" className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="team-slip-amount" />
        <label className="bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg px-3 py-2 text-sm cursor-pointer flex items-center gap-1.5 truncate">
          <Upload className="w-3.5 h-3.5" /> {file ? file.name : (at('teamSlipPdf') || 'PDF du bulletin')}
          <input type="file" accept="application/pdf" hidden onChange={e => setFile(e.target.files?.[0] || null)} data-testid="team-slip-file" />
        </label>
      </div>
      <button
        onClick={send}
        disabled={sending || !file}
        className="w-full py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2"
        data-testid="team-slip-send"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
        {at('teamSlipSend') || 'Envoyer par email'}
      </button>
    </div>
  );
};

// -------------------- Generate professional pay slip (v50.5) --------------------
// The manager fills a structured form; the backend renders a professional PDF
// with reportlab, archives it, and (optionally) emails it. Bytes are also
// returned so the manager can download the PDF immediately.
const b64ToBlobUrl = (b64, mime = 'application/pdf') => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mime }));
};

const DEFAULT_LINES = [
  { label: 'Salaire de base', base: null, rate: '', amount: null, kind: 'earning' },
  { label: 'Heures supplémentaires', base: null, rate: '', amount: null, kind: 'earning' },
  { label: 'Prime', base: null, rate: '', amount: null, kind: 'earning' },
  { label: 'Sécurité sociale — Maladie', base: null, rate: '0,75 %', amount: null, kind: 'deduction' },
  { label: 'Assurance chômage', base: null, rate: '2,40 %', amount: null, kind: 'deduction' },
  { label: 'Retraite complémentaire', base: null, rate: '3,15 %', amount: null, kind: 'deduction' },
  { label: 'CSG déductible', base: null, rate: '6,80 %', amount: null, kind: 'deduction' },
  { label: 'CSG/CRDS non déductible', base: null, rate: '2,90 %', amount: null, kind: 'deduction' },
];

const GenerateSalarySlipBox = ({ at, memberId, member }) => {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [currency, setCurrency] = useState('€');
  const [grossSalary, setGrossSalary] = useState('');
  const [netBeforeTax, setNetBeforeTax] = useState('');
  const [totalDeductions, setTotalDeductions] = useState('');
  const [incomeTax, setIncomeTax] = useState('');
  const [netPaid, setNetPaid] = useState('');
  const [ytdGross, setYtdGross] = useState('');
  const [ytdNet, setYtdNet] = useState('');
  const [notes, setNotes] = useState('');
  const [empName, setEmpName] = useState(member?.name || '');
  const [empSs, setEmpSs] = useState('');
  const [empPosition, setEmpPosition] = useState(member?.profile?.job_type || '');
  const [empContract, setEmpContract] = useState(member?.profile?.contract_type || 'CDI');
  const [empHours, setEmpHours] = useState(member?.profile?.hours_per_week || '');
  const [empCoef, setEmpCoef] = useState('');
  const [empAddress, setEmpAddress] = useState('');
  const [erName, setErName] = useState('Destine');
  const [erAddress, setErAddress] = useState('');
  const [erSiret, setErSiret] = useState('');
  const [lines, setLines] = useState(DEFAULT_LINES);
  const [sendByEmail, setSendByEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  const num = (v) => (v === '' || v === null || v === undefined) ? null : Number(String(v).replace(',', '.'));

  const updateLine = (i, patch) => {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  };
  const addLine = () => setLines(prev => [...prev, { label: '', base: null, rate: '', amount: null, kind: 'info' }]);
  const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

  const generate = async () => {
    if (!/^\d{4}-\d{2}$/.test(period)) { toast.error(at('teamSlipPeriodInvalid') || 'Période invalide (YYYY-MM)'); return; }
    if (!empName.trim()) { toast.error('Nom du salarié requis'); return; }
    setBusy(true);
    try {
      const payload = {
        period,
        pay_date: payDate,
        currency,
        employer: {
          name: erName || 'Destine',
          address: erAddress,
          siret: erSiret,
        },
        employee: {
          full_name: empName,
          address: empAddress,
          ss_number: empSs,
          position: empPosition,
          contract_type: empContract,
          hours_per_week: num(empHours),
          coefficient: empCoef,
        },
        lines: lines
          .filter(l => (l.label || '').trim())
          .map(l => ({
            label: l.label,
            base: num(l.base),
            rate: l.rate || '',
            amount: num(l.amount),
            kind: l.kind || 'info',
          })),
        gross_salary: num(grossSalary),
        total_deductions: num(totalDeductions),
        net_before_tax: num(netBeforeTax),
        income_tax: num(incomeTax),
        net_paid: num(netPaid),
        ytd: (ytdGross || ytdNet) ? { gross: num(ytdGross), net: num(ytdNet) } : null,
        notes,
        send_by_email: sendByEmail,
        archive: true,
      };
      const r = await axios.post(`/api/admin/team/${memberId}/salary-slip/generate`, payload);
      const b64 = r.data?.pdf_base64;
      if (b64) {
        const url = b64ToBlobUrl(b64);
        const a = document.createElement('a');
        a.href = url;
        a.download = r.data.filename || `bulletin_${period}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
      if (r.data?.sent) toast.success((at('teamSlipSent') || 'Bulletin envoyé à {to}').replace('{to}', r.data.to));
      else toast.success(at('teamSlipStored') || 'Bulletin généré et archivé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-3 rounded-lg bg-fuchsia-500/10 border border-fuchsia-400/25 mt-3" data-testid="team-slip-generate-box">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between text-sm font-medium text-fuchsia-200"
        data-testid="team-slip-generate-toggle"
      >
        <span className="flex items-center gap-2"><FileSignature className="w-4 h-4" /> Générer un bulletin professionnel (PDF)</span>
        <ChevronRight className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {/* Period + pay date + currency */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className="text-[11px] text-white/60 block mb-1">Période</label>
              <input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="gen-slip-period" />
            </div>
            <div>
              <label className="text-[11px] text-white/60 block mb-1">Date de paiement</label>
              <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="gen-slip-paydate" />
            </div>
            <div>
              <label className="text-[11px] text-white/60 block mb-1">Devise</label>
              <input type="text" value={currency} onChange={e => setCurrency(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          {/* Employer block */}
          <details className="rounded-lg bg-white/5 border border-white/10 p-2" open>
            <summary className="text-xs font-semibold text-white/70 cursor-pointer">Employeur</summary>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
              <input placeholder="Nom" value={erName} onChange={e => setErName(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
              <input placeholder="SIRET" value={erSiret} onChange={e => setErSiret(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
              <input placeholder="Adresse" value={erAddress} onChange={e => setErAddress(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm md:col-span-3" />
            </div>
          </details>

          {/* Employee block */}
          <details className="rounded-lg bg-white/5 border border-white/10 p-2" open>
            <summary className="text-xs font-semibold text-white/70 cursor-pointer">Salarié</summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              <input placeholder="Nom complet" value={empName} onChange={e => setEmpName(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="gen-slip-empname" />
              <input placeholder="N° sécurité sociale" value={empSs} onChange={e => setEmpSs(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
              <input placeholder="Poste" value={empPosition} onChange={e => setEmpPosition(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
              <select value={empContract} onChange={e => setEmpContract(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm">
                {CONTRACT_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input type="number" step="0.5" placeholder="Heures / semaine" value={empHours} onChange={e => setEmpHours(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
              <input placeholder="Coefficient" value={empCoef} onChange={e => setEmpCoef(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
              <input placeholder="Adresse" value={empAddress} onChange={e => setEmpAddress(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm md:col-span-2" />
            </div>
          </details>

          {/* Lines table */}
          <div className="rounded-lg bg-white/5 border border-white/10 p-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-white/70">Rubriques (gains / retenues)</span>
              <button type="button" onClick={addLine} className="text-xs px-2 py-1 rounded-full bg-fuchsia-500/25 text-fuchsia-200 flex items-center gap-1" data-testid="gen-slip-add-line">
                <Plus className="w-3 h-3" /> Ajouter
              </button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`gen-slip-line-${i}`}>
                  <input placeholder="Libellé" value={l.label} onChange={e => updateLine(i, { label: e.target.value })} className="col-span-4 bg-white/10 border border-white/15 rounded-md px-2 py-1.5 text-xs" />
                  <input placeholder="Base" value={l.base ?? ''} onChange={e => updateLine(i, { base: e.target.value })} className="col-span-2 bg-white/10 border border-white/15 rounded-md px-2 py-1.5 text-xs" />
                  <input placeholder="Taux" value={l.rate ?? ''} onChange={e => updateLine(i, { rate: e.target.value })} className="col-span-2 bg-white/10 border border-white/15 rounded-md px-2 py-1.5 text-xs" />
                  <input placeholder="Montant" value={l.amount ?? ''} onChange={e => updateLine(i, { amount: e.target.value })} className="col-span-2 bg-white/10 border border-white/15 rounded-md px-2 py-1.5 text-xs" />
                  <select value={l.kind} onChange={e => updateLine(i, { kind: e.target.value })} className="col-span-1 bg-white/10 border border-white/15 rounded-md px-1 py-1.5 text-xs">
                    <option value="earning">+</option>
                    <option value="deduction">-</option>
                    <option value="info">i</option>
                  </select>
                  <button type="button" onClick={() => removeLine(i)} className="col-span-1 p-1 rounded-md hover:bg-red-500/25 text-red-300" data-testid={`gen-slip-line-remove-${i}`}>
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div>
              <label className="text-[11px] text-white/60 block mb-1">Brut</label>
              <input value={grossSalary} onChange={e => setGrossSalary(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="gen-slip-gross" />
            </div>
            <div>
              <label className="text-[11px] text-white/60 block mb-1">Retenues</label>
              <input value={totalDeductions} onChange={e => setTotalDeductions(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[11px] text-white/60 block mb-1">Net avant impôt</label>
              <input value={netBeforeTax} onChange={e => setNetBeforeTax(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[11px] text-white/60 block mb-1">PAS</label>
              <input value={incomeTax} onChange={e => setIncomeTax(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[11px] text-white/60 block mb-1">Net payé</label>
              <input value={netPaid} onChange={e => setNetPaid(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="gen-slip-netpaid" />
            </div>
          </div>

          {/* YTD */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-white/60 block mb-1">Cumul brut annuel</label>
              <input value={ytdGross} onChange={e => setYtdGross(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[11px] text-white/60 block mb-1">Cumul net annuel</label>
              <input value={ytdNet} onChange={e => setYtdNet(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[11px] text-white/60 block mb-1">Notes / mentions</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" />
          </div>

          <label className="flex items-center gap-2 text-xs text-white/80">
            <input type="checkbox" checked={sendByEmail} onChange={e => setSendByEmail(e.target.checked)} data-testid="gen-slip-email-toggle" />
            Envoyer aussi par email au salarié
          </label>

          <button
            onClick={generate}
            disabled={busy}
            className="w-full py-2 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2"
            data-testid="gen-slip-generate-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Générer et télécharger le PDF
          </button>
        </div>
      )}
    </div>
  );
};

// -------------------- Partners section --------------------
const PartnersSection = ({ at, partners, reload }) => {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('partner');
  const [note, setNote] = useState('');
  const [share, setShare] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await axios.post('/api/admin/partners', {
        name: name.trim(),
        kind,
        note,
        share_percent: share ? parseFloat(share) : null,
      });
      setName(''); setNote(''); setShare('');
      toast.success(at('teamPartnerAdded') || 'Ajouté');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(at('teamPartnerConfirmDelete') || `Supprimer ${p.name} ?`)) return;
    try {
      await axios.delete(`/api/admin/partners/${p.id}`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
        <h4 className="text-sm font-semibold mb-2">{at('teamPartnerNew') || 'Ajouter un partenaire / actionnaire'}</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Nom" className="md:col-span-2 bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="partner-name-input" />
          <select value={kind} onChange={e => setKind(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="partner-kind-input">
            <option value="partner">Partenaire</option>
            <option value="shareholder">Actionnaire</option>
          </select>
          <input value={share} onChange={e => setShare(e.target.value)} placeholder="% détenu (opt.)" type="number" min="0" max="100" step="0.01" className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="partner-share-input" />
        </div>
        <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Note (opt.)" rows={2} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm mb-2" data-testid="partner-note-input" />
        <button onClick={add} disabled={busy || !name.trim()} className="px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-1.5" data-testid="partner-add-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Handshake className="w-4 h-4" />}
          {at('teamPartnerAdd') || 'Ajouter'}
        </button>
      </div>

      {partners.length === 0 ? (
        <div className="text-center py-8 text-white/40 border border-dashed border-white/10 rounded-xl">
          {at('teamPartnersEmpty') || 'Aucun partenaire ou actionnaire.'}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="partners-list">
          {partners.map(p => (
            <li key={p.id} className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-start gap-3" data-testid={`partner-${p.id}`}>
              <div className="w-8 h-8 rounded-full bg-fuchsia-500/25 text-fuchsia-200 flex items-center justify-center flex-shrink-0"><Handshake className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-white text-sm">{p.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.kind === 'shareholder' ? 'bg-amber-500/25 text-amber-200' : 'bg-emerald-500/25 text-emerald-200'}`}>
                    {p.kind === 'shareholder' ? (at('teamPartnerShareholder') || 'Actionnaire') : (at('teamPartnerType') || 'Partenaire')}
                  </span>
                  {p.share_percent != null && <span className="text-[10px] text-white/50">{p.share_percent}%</span>}
                </div>
                {p.note && <div className="text-xs text-white/70 mt-1 whitespace-pre-wrap">{p.note}</div>}
              </div>
              <button onClick={() => remove(p)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-300 flex-shrink-0" data-testid={`partner-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminTeamPanel;
